type LanguageCode = "de" | "en";

namespace e5 {
    export class DateUtil {
        /**
         * 
         * @param value A UTC date formated string like: 2019-01-15 (YYYY-MM-DD)
         * 
         * Important: without time!
         */
        public static fromString(value: string, time: string = "T00:00:00.000Z"): Date {
            return new Date(value + time);
        }

        public static fromTime(time: number): Date {
            var result = new Date();
            result.setTime(time);
            return result;
        }

        public static round(date: Date): Date {
            return this.fromTime(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 2, 0, 0, 0));
        }

        public static toString(date: Date): string {
            return date.getUTCFullYear() + "-" + e5.Text.fillHead((date.getUTCMonth() + 1) + "", 2, "0") + "-" + e5.Text.fillHead(date.getUTCDate() + "", 2, "0");
        }

        public static toKey(date: Date): string {
            return date.getUTCFullYear() + "-" + (date.getUTCMonth() + 1) + "-" + date.getUTCDate();
        }

        /**
         * a > b = 1 
         * a == b = 0 
         * a < b = -1 
         */
        public static compare(a: Date, b: Date, ignoreTime: boolean = false): number {
            if (!ignoreTime) {
                var at = a.getTime();
                var bt = b.getTime();
                if (at > bt) {
                    return 1;
                }
                if (at < bt) {
                    return -1;
                }
                return 0;
            }

            if (a.getUTCFullYear() > b.getUTCFullYear())
                return 1;
            if (a.getUTCFullYear() < b.getUTCFullYear())
                return -1;
            //Same year -->>

            if (a.getUTCMonth() > b.getUTCMonth())
                return 1;
            if (a.getUTCMonth() < b.getUTCMonth())
                return -1;
            //Same year and month -->>

            if (a.getUTCDate() > b.getUTCDate())
                return 1;
            if (a.getUTCDate() < b.getUTCDate())
                return -1;

            //Same year, month, day -->>
            return 0;
        }

        /**
         * a >= start && a <= end = true 
         */
        public static isBetween(a: Date, start: Date, end: Date, ignoreTime: boolean = false): boolean {
            return this.greaterEqual(a, start, ignoreTime) && this.smallerEqual(a, end, ignoreTime);
        }

        /**
         * a > b = true 
         */
        public static greater(a: Date, b: Date, ignoreTime: boolean = false): boolean {
            return this.compare(a, b, ignoreTime) === 1;
        }

        /**
         * a >= b = true 
         */
        public static greaterEqual(a: Date, b: Date, ignoreTime: boolean = false): boolean {
            return this.compare(a, b, ignoreTime) !== -1;
        }

        /**
         * a < b = true 
         */
        public static smaller(a: Date, b: Date, ignoreTime: boolean = false): boolean {
            return this.compare(a, b, ignoreTime) === -1;
        }

        /**
         * a <= b = true 
         */
        public static smallerEqual(a: Date, b: Date, ignoreTime: boolean = false): boolean {
            return this.compare(a, b, ignoreTime) !== 1;
        }

        /**
         * a == b = true 
         */
        public static equal(a: Date, b: Date, ignoreTime: boolean = false): boolean {
            return this.compare(a, b, ignoreTime) === 0;
        }

        public static dayDiff(a: Date, b: Date): number {
            var a = DateUtil.round(a);
            var b = DateUtil.round(b);
            var t = a.getTime() - b.getTime();
            const diffTime = Math.abs(t);
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) * (t < 0 ? -1 : 1);
        }

        public static timeToDays(time: number): number {
            return Math.floor(time / (1000 * 60 * 60 * 24));
        }

        public static timeToHours(time: number): number {
            return Math.floor(time / (1000 * 60 * 60)) % 24;
        }

        public static timeToMinutes(time: number): number {
            return Math.floor(time / (1000 * 60)) % 60;
        }

        public static timeToSeconds(time: number): number {
            return Math.floor(time / 1000) % 60;
        }

        public static nansenToMosaic(date: Date): Date {
            var d = new Date(date);
            d.setUTCFullYear(d.getUTCFullYear() + (2019 - 1893));
            return d;
        }

        public static mosaicToNansen(date: Date): Date {
            var d = new Date(date);
            d.setUTCFullYear(d.getUTCFullYear() - (2019 - 1893));
            return d;
        }

        public static toStringReadable(date: Date, language: LanguageCode): string {
            return `${date.getUTCDate()}. ${e5.Text.month(date.getUTCMonth(), language)} ${date.getUTCFullYear()}`;
        }

        public static toStringReadableLocal(date: Date, language: LanguageCode): string {
            return `${date.getDate()}. ${e5.Text.month(date.getMonth(), language)} ${date.getFullYear()}`;
        }

        public static toStringReadableShort(date: Date, language: LanguageCode): string {
            return `${date.getUTCDate()}. ${e5.Text.month(date.getUTCMonth(), language).substr(0, 3)} ${date.getUTCFullYear()}`;
        }

        public static toStringReadableShortLocal(date: Date, language: LanguageCode): string {
            return `${date.getDate()}. ${e5.Text.month(date.getMonth(), language).substr(0, 3)} ${date.getFullYear()}`;
        }

        public static toTimeStringHoursMinutes(hours: number, minutes: number, language: LanguageCode): string {
            if (language === "de") {
                return hours + ":" + (minutes < 10 ? "0" + minutes : minutes) + " Uhr";

            } else {
                //@source https://stackoverflow.com/questions/8888491/how-do-you-display-javascript-datetime-in-12-hour-am-pm-format
                var ampm = hours >= 12 ? 'pm' : 'am';
                hours = hours % 12;
                hours = hours ? hours : 12; // the hour '0' should be '12'
                var minutesStr = minutes < 10 ? '0' + minutes : minutes;
                return hours + ':' + minutesStr + ' ' + ampm;
            }

            return ``;
        }

        public static toTimeString(date: Date, language: LanguageCode): string {
            return this.toTimeStringHoursMinutes(date.getHours(), date.getMinutes(), language);
        }

        public static toTimeStringUTC(date: Date, language: LanguageCode): string {
            return this.toTimeStringHoursMinutes(date.getUTCHours(), date.getUTCMinutes(), language);
        }

        /**
         * Modified/Fixed source from Stackoverflow:
         * @see https://stackoverflow.com/questions/2536379/difference-in-months-between-two-dates-in-javascript
         */
        public static monthDiff(d1: Date, d2: Date): number {
            var months = (d2.getUTCFullYear() - d1.getUTCFullYear()) * 12;
            months -= d1.getUTCMonth();
            months += d2.getUTCMonth();
            return months;
        }

        public static clamp(d: Date, min: Date, max: Date): Date {
            var r = new Date();
            r.setTime(e5.Calc.clamp(d.getTime(), min.getTime(), max.getTime()));
            return r;
        }

        public static daysOfMonth(date: Date): number {
            return (new Date(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
        }
    }
}